import { ItemListPosition } from '@capsicum/common';
import { ButtonInSection, ButtonSection, InfoCard, NetworkFeeCard, Text } from '@capsicum/common-web';
import { ChilizChainCircleIcon } from '@capsicum/common-web/icons';
import { ApprovedFooter } from '@capsicum/features/regulations/web';
import styled from 'styled-components';

import { WCPledgeHeaderWarning } from './WCPledgeCommon.styled';

export enum WCPledgeReviewTransactionType {
	CLAIM = 'claim',
	UNSTAKE = 'unstake'
}

type BaseWCPledgeReviewProps = {
	isConfirmButtonDisabled: boolean;
	isLoading: boolean;
	sendTransactionHandler: () => void;
	texts: {
		action: string;
		button: string;
		disclaimer: string;
		networkFee: {
			title: string;
			tooltip: string;
		};
		smartContractAddress: {
			title: string;
			tooltip: string;
		};
		subtitle: string;
		title: string;
		transactionValue: string;
	};
	transactionDetails: {
		gasFeeEstimate?: number;
		gasFeeEstimateFiat?: string;
		isTransactionPending?: boolean;
		smartContractAddress?: string;
	};
};

type WCPledgeReviewProps =
	| (BaseWCPledgeReviewProps & {
			operationDetails: {
				currencyLogo: string;
				stakedOn: string;
			};
			texts: {
				stakedOn: string;
			};
			transactionType: WCPledgeReviewTransactionType.UNSTAKE;
	  })
	| (BaseWCPledgeReviewProps & {
			transactionType: WCPledgeReviewTransactionType.CLAIM;
	  });

export const WCPledgeReview = (props: WCPledgeReviewProps) => {
	return (
		<S.PageWrapper>
			<S.Container>
				<WCPledgeHeaderWarning page="wcPledge" />
				<S.Heading>{props.texts.title}</S.Heading>
				<S.Subheading variant="text2">{props.texts.subtitle}</S.Subheading>
				<div>
					<InfoCard
						icon={<ChilizChainCircleIcon />}
						isLoading={props.isLoading}
						itemListPosition={ItemListPosition.START}
						mainRightText={props.texts.transactionValue}
						mainRightTitleSize="large"
						showDivider
						testID="wcPledge-review-amountSelected"
						title={props.texts.action}
					/>
					{props.transactionType === WCPledgeReviewTransactionType.UNSTAKE && (
						<InfoCard
							icon={props.operationDetails.currencyLogo || <ChilizChainCircleIcon />}
							isLoading={props.isLoading}
							itemListPosition={ItemListPosition.END}
							mainRightText={props.operationDetails.stakedOn}
							mainRightTitleSize="large"
							testID="wcPledge-review-stakedOn"
							title={props.texts.stakedOn}
						/>
					)}
				</div>
				<NetworkFeeCard
					feeAmount={props.transactionDetails.gasFeeEstimate}
					feePrice={props.transactionDetails.gasFeeEstimateFiat?.toString()}
					testID="wcPledge-review-networkFee"
					variant="free"
				/>
				<InfoCard
					isLoading={props.isLoading}
					isMainRightTextCopyable
					mainRightText={props.transactionDetails?.smartContractAddress}
					testID="wcPledge-review-smartContract"
					title={props.texts?.smartContractAddress?.title}
					tooltipText={props.texts.smartContractAddress.tooltip}
				/>
				<ApprovedFooter page="wcPledge" />
			</S.Container>

			<S.Disclaimer variant="text3">{props.texts.disclaimer}</S.Disclaimer>

			<S.StyledButtonSection>
				<ButtonInSection
					disabled={props.isConfirmButtonDisabled}
					isLoading={props.isLoading}
					testID="wcPledge-review-button"
					text={props.texts.button}
					onClick={props.sendTransactionHandler}
				/>
			</S.StyledButtonSection>
		</S.PageWrapper>
	);
};

namespace S {
	export const Heading = styled.h1`
		margin: 0;
		${({ theme }) => theme.fonts.headings.title5};
		color: ${({ theme }) => theme.colors.text.main.enabled};
	`;

	export const Subheading = styled(Text)`
		color: ${({ theme }) => theme.colors.text.main.subtle};
	`;

	export const StyledButtonSection = styled(ButtonSection)<{ $hasMarginTop?: boolean }>`
		${({ $hasMarginTop }) => !$hasMarginTop && 'margin-top: 0;'};
	`;

	export const PageWrapper = styled.div`
		width: 100%;
		display: flex;
		flex-direction: column;
		justify-content: space-between;
		padding: ${({ theme }) => theme.spacings.large} ${({ theme }) => theme.spacings.small}
			${({ theme }) => theme.spacings.xxxlarge} ${({ theme }) => theme.spacings.small};

		${({ theme }) => theme.breakpoints.queries.above.tabletSmallDesktop} {
			padding: ${({ theme }) => theme.spacings.xxlarge} 0;
			justify-content: flex-start;
		}
	`;

	export const Container = styled.div`
		display: flex;
		flex-direction: column;
		row-gap: ${({ theme }) => theme.spacings.xsmall};
		margin-bottom: ${({ theme }) => theme.spacings.large};
	`;

	export const Disclaimer = styled(Text)`
		margin-bottom: ${({ theme }) => theme.spacings.large};
		color: ${({ theme }) => theme.colors.text.main.subtle};
	`;
}
